import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  PriceInterval,
  ProductPrice,
  ProductType,
  RegistrationPaymentInfo,
} from 'src/app/interfaces/user';
import { UserService } from '../user/user.service';
import { FeatureFlagsService } from '../feature-flag/feature-flag.service';
import { PRODUCT_PRICE_ID_ENTRIES, formatProductPrice } from 'src/app/constants/payment-config.constants';

/** The plan-card availability the backend's product list defines. */
export interface ProductAvailability {
  basicPlus: boolean;
  premium: boolean;
  smartTag: boolean;
}

/**
 * Owns the per-user /api/registration/me/payment config: which products are available, their prices,
 * and the derived card availability, display prices and checkout price resolution. Cached per session.
 */
@Injectable({ providedIn: 'root' })
export class PaymentConfigService {
  private cachedConfig: RegistrationPaymentInfo | null = null;
  private isBrowser: boolean;

  constructor(
    private userService: UserService,
    private featureFlagService: FeatureFlagsService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  public get config(): RegistrationPaymentInfo | null {
    return this.cachedConfig;
  }

  /**
   * Fetches the payment config once and caches it. Returns null without a request when the
   * isRegistrationPaymentConfigEnabled flag is off or on the server (the call needs the
   * localStorage token).
   */
  public load(): Observable<RegistrationPaymentInfo | null> {
    if (
      !this.isBrowser ||
      !this.featureFlagService.isEnabled('isRegistrationPaymentConfigEnabled')
    ) {
      return of(null);
    }
    if (this.cachedConfig) {
      return of(this.cachedConfig);
    }
    return this.userService
      .getRegistrationPaymentConfig()
      .pipe(
        tap((info) => (this.cachedConfig = info)),
        catchError(() => of(null))
      );
  }

  public clear(): void {
    this.cachedConfig = null;
  }

  /**
   * The ProductType ids the backend allows, mapped from the availableProducts keys (product names).
   * Null when there is no config, so consumers fall back to their user-based card logic. Unknown
   * product names (not in the ProductType enum) are ignored.
   */
  public getAvailableProductIds(): number[] | null {
    const products = this.cachedConfig?.availableProducts;
    if (!products) {
      return null;
    }
    const productIds: ProductType[] = [];
    for (const name of Object.keys(products)) {
      const productId = ProductType[name as keyof typeof ProductType];
      if (typeof productId === 'number') {
        productIds.push(productId);
      }
    }
    return productIds.length ? productIds : null;
  }

  /**
   * Maps a product-id list onto the three plan-card availability flags. Returns null when there
   * is no list, so callers keep the availability they already derived from the user's own plan
   * instead of falling open. Takes the list as an argument because the pricing tables receive it
   * as an input from their parent.
   */
  public getProductAvailability(productIds: number[] | null): ProductAvailability | null {
    if (!productIds) {
      return null;
    }
    const productIdSet = new Set(productIds);
    return {
      basicPlus: productIdSet.has(ProductType.BasicPlus),
      premium: productIdSet.has(ProductType.Premium),
      smartTag: productIdSet.has(ProductType.Locator),
    };
  }

  /**
   * Whether the backend offers a product. True when there is no config (no /payment call, an error,
   * or an empty availableProducts), so callers fall back to showing the product rather than hiding
   * it.
   */
  public isProductAvailable(productType: ProductType): boolean {
    const productIds = this.getAvailableProductIds();
    return !productIds || productIds.includes(productType);
  }

  /** The backend price for a ProductType and billing interval, or null when the config omits it. */
  public getProductPrice(productType: ProductType, interval: PriceInterval): ProductPrice | null {
    const productName = ProductType[productType];
    const prices = this.cachedConfig?.availableProducts?.[productName];
    return prices?.find((price) => price.interval === interval) ?? null;
  }

  /**
   * The backend yearly price for a product formatted for display (for example '£39'),
   * or null when the config does not include it so consumers
   * fall back to their config.json price text.
   */
  public getYearlyDisplayPrice(productType: ProductType): string | null {
    const price = this.getProductPrice(productType, 'year');
    return price ? formatProductPrice(price) : null;
  }

  /**
   * The monthly display price for a product, dictated by /payment
   * returns the backend monthly price, or null
   * when the backend doesn't offer one, so consumers
   * hide/disable monthly instead of falling back to config.json. 
   * staticFallback - backend does not cover this product (feature
   * disabled, user unauthenticated, or product absent from availableProducts).
   */
  public getMonthlyDisplayPrice(productType: ProductType, staticFallback: string): string | null {
    const productName = ProductType[productType];
    const isBackendDrivenProduct = !!this.cachedConfig?.availableProducts?.[productName]?.length;
    if (!isBackendDrivenProduct) {
      return staticFallback;
    }
    const price = this.getProductPrice(productType, 'month');
    return price ? formatProductPrice(price) : null;
  }

  /**
   * Swaps each default price ID for the backend's price ID of the same product and billing interval
   * when /payment provides one, so every checkout follows backend pricing.
   * Price IDs that don't match a known entry pass through unchanged.
   */
  public resolvePriceIds(priceIds: string[]): string[] {
    if (!this.cachedConfig) {
      return priceIds;
    }
    return priceIds.map((priceId) => {
      const entry = PRODUCT_PRICE_ID_ENTRIES.find((entry) => entry.defaultPriceId === priceId);
      if (!entry) {
        return priceId;
      }
      return this.getProductPrice(entry.productType, entry.interval)?.priceId ?? priceId;
    });
  }
}
